import React, { useState, useEffect } from "react";
import { Save, User, Briefcase, Globe, Mail, AlignLeft, Camera, Linkedin, Twitter } from "lucide-react";
import FormHeader from "../Common/FormHeader"; 

const FormSpeaker = ({ onBack, onSave, initialData = null }) => {
  // Definisi State Awal untuk Speaker Management
  const [formData, setFormData] = useState({
    name: "",
    title: "", // e.g. Senior Cardiologist
    email: "",
    organization: "",
    biography: "",
    specialization: "",
    socialLinkedin: "",
    socialTwitter: "",
    image: null
  });

  // Sinkronisasi data saat Edit Mode sesuai pola yang kita gunakan
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        name: initialData.name || "",
        title: initialData.title || "",
        email: initialData.email || "",
        organization: initialData.organization || "",
        biography: initialData.biography || "",
        specialization: initialData.specialization || "",
        socialLinkedin: initialData.socialLinkedin || "",
        socialTwitter: initialData.socialTwitter || "",
        image: initialData.image || null
      });
    } else {
      setFormData({
        name: "", title: "", email: "",
        organization: "", biography: "", specialization: "",
        socialLinkedin: "", socialTwitter: "", image: null
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5 animate-in fade-in duration-500">
      
      {/* HEADER DENGAN TOMBOL BACK */}
      <FormHeader 
        title={initialData ? "Edit Speaker Profile" : "Register New Speaker"}
        subtitle="Provide professional details and credentials for the summit speaker"
        onBack={onBack} 
      />

      <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* KOLOM KIRI: Identitas Utama */}
          <div className="space-y-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Full Name & Title</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. Prof. Dr. Andri Sebastian"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Professional Role</label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. Lead Technology Consultant"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="email" required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="speaker@siloam.com"
                />
              </div>
            </div>
          </div>

          {/* KOLOM KANAN: Organisasi & Medsos */}
          <div className="space-y-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Organization / Hospital</label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({...formData, organization: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. Siloam Hospitals Group"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">LinkedIn (Optional)</label>
                <div className="relative">
                  <Linkedin className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="text"
                    value={formData.socialLinkedin}
                    onChange={(e) => setFormData({...formData, socialLinkedin: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 rounded-xl text-[12px] outline-none"
                    placeholder="linkedin.com/in/..."
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Twitter (Optional)</label>
                <div className="relative">
                  <Twitter className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="text"
                    value={formData.socialTwitter}
                    onChange={(e) => setFormData({...formData, socialTwitter: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 rounded-xl text-[12px] outline-none"
                    placeholder="@username"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Specialization</label>
              <select 
                value={formData.specialization}
                onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                className="w-full px-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
              >
                <option value="">Select Specialization</option>
                <option value="Cardiology">Cardiology</option>
                <option value="Health Tech">Health Tech</option>
                <option value="Surgery">Surgery</option>
                <option value="Diagnostics">Diagnostics</option>
              </select>
            </div>
          </div>

          {/* Biography */}
          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Biography & Achievements</label>
            <div className="relative">
              <AlignLeft className="absolute left-4 top-4 text-stone-400" size={14} />
              <textarea 
                rows="4"
                value={formData.biography}
                onChange={(e) => setFormData({...formData, biography: e.target.value})}
                className="w-full pl-10 pr-4 py-4 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none resize-none leading-relaxed focus:border-blue-500 text-slate-800 dark:text-white"
                placeholder="Write a brief professional background..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-6 border-t border-stone-50 dark:border-white/5 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onBack}
            className="px-8 py-3 rounded-xl text-sm font-semibold text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-white/5 transition-all"
          >
            Discard
          </button>
          <button 
            type="submit"
            className="px-10 py-3 bg-[#0F172A] dark:bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-700 transition-all flex items-center gap-2"
          >
            <Save size={18} />
            {initialData ? "Update Speaker" : "Save Speaker"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormSpeaker;